import { useState, useEffect, useRef, useCallback } from "react";
import { ComboPopup } from "../components/Engagement";
import { oddOneOutGames, sequenceGames, memoryPairs, riddleGames } from "./GameData";

// Find the Odd One Out Game
const ODD_SESSION_SIZE = 6;

function buildOddSession(profileId, difficulty) {
  const pool = oddOneOutGames[difficulty];
  const key = `odd_seen_${profileId || "guest"}_${difficulty}`;
  let seen = [];
  try {
    seen = JSON.parse(localStorage.getItem(key) || "[]");
    if (!Array.isArray(seen)) seen = [];
  } catch (e) { console.warn("OddOneOut history read failed:", e); seen = []; }

  const allIndices = pool.map((_, i) => i);
  let unseen = allIndices.filter((i) => !seen.includes(i));
  if (unseen.length < ODD_SESSION_SIZE) {
    seen = [];
    unseen = allIndices;
  }
  const chosen = shuffleArr(unseen).slice(0, Math.min(ODD_SESSION_SIZE, pool.length));
  try { localStorage.setItem(key, JSON.stringify([...seen, ...chosen])); } catch (e) { console.warn("OddOneOut history save failed:", e); }

  return chosen.map((idx) => {
    const g = pool[idx];
    const correctItem = g.items[g.answer];
    const shuffledItems = shuffleArr(g.items);
    return { ...g, items: shuffledItems, answer: shuffledItems.indexOf(correctItem) };
  });
}

export function OddOneOutGame({ t, lang, ageGroup, profileId, onBack, onComplete }) {
  const difficulty = ageGroup === "6-7" ? "easy" : "hard";
  const [games] = useState(() => buildOddSession(profileId, difficulty));

  const [currentLevel, setCurrentLevel] = useState(0);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const maxComboRef = useRef(0);
  const [feedback, setFeedback] = useState(null);
  const [showHint, setShowHint] = useState(false);

  const currentGame = games[currentLevel];

  const handleAnswer = (index) => {
    if (feedback) return;
    
    const isCorrect = index === currentGame.answer;
    if (!isCorrect) setCombo(0);
    setFeedback(isCorrect ? "correct" : "wrong");
    
    if (isCorrect) {
      setScore(score + 1);
      maxComboRef.current = Math.max(maxComboRef.current, combo + 1);
      setCombo(combo + 1);
    }
    
    setTimeout(() => {
      setFeedback(null);
      setShowHint(false);
      if (currentLevel < games.length - 1) {
        setCurrentLevel(currentLevel + 1);
      } else {
        if (onComplete) onComplete({ score: score + (isCorrect ? 1 : 0), total: games.length, maxCombo: maxComboRef.current });
      }
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-500 via-teal-500 to-cyan-500 p-4">
      <div className="max-w-lg mx-auto">
        <button
          onClick={onBack}
          className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4"
          data-testid="game-back-btn"
        >
          ← {t.backHome}
        </button>
        
        <ComboPopup combo={combo} />
        <h2 className="text-2xl font-bold text-white text-center mb-2" data-testid="oddoneout-title">🔍 {t.findOddOne}</h2>
        
        {/* Progress */}
        <div className="flex justify-between text-white mb-4">
          <span data-testid="oddoneout-level">{t.level} {currentLevel + 1}/{games.length}</span>
          <span data-testid="oddoneout-score">{t.score}: {score}</span>
        </div>
        
        <div className="bg-white rounded-3xl p-6 shadow-xl">
          <p className="text-xl text-gray-700 text-center mb-6 font-bold" data-testid="oddoneout-prompt">
            {t.whichDoesntBelong}
          </p>
          
          {/* Options */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {currentGame.items.map((item, idx) => (
              <button
                key={`${item}-${idx}`}
                onClick={() => handleAnswer(idx)}
                disabled={feedback !== null}
                data-testid={`oddoneout-option-${idx}`}
                className={`
                  p-6 rounded-2xl text-5xl font-bold transition-all
                  ${feedback === null 
                    ? "bg-gray-100 hover:bg-gray-200 hover:scale-105" 
                    : idx === currentGame.answer
                      ? "bg-green-100 ring-4 ring-green-500"
                      : feedback === "wrong" && idx !== currentGame.answer
                        ? "bg-gray-100"
                        : "bg-gray-100"
                  }
                `}
              >
                {item}
              </button>
            ))}
          </div>
          
          {/* Feedback */}
          {feedback && (
            <div className={`text-center text-xl font-bold ${feedback === "correct" ? "text-green-500" : "text-red-500"}`}>
              {feedback === "correct" ? `✅ ${t.excellent}` : `❌ ${t.tryAgain}`}
            </div>
          )}
          
          {/* Hint */}
          {!feedback && (
            <button
              onClick={() => setShowHint(!showHint)}
              className="w-full py-2 text-gray-500 hover:text-gray-700"
            >
              💡 {showHint ? (lang === "it" ? currentGame.hint_it : currentGame.hint_en) : "Suggerimento?"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Sequence Game
export function SequenceGame({ t, lang, ageGroup, onBack, onComplete }) {
  const difficulty = ageGroup === "6-7" ? "easy" : "hard";
  const games = sequenceGames[difficulty];
  
  const [currentLevel, setCurrentLevel] = useState(0);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const maxComboRef = useRef(0);
  const [feedback, setFeedback] = useState(null);

  const currentGame = games[currentLevel];

  const handleAnswer = (index) => {
    if (feedback) return;
    
    const isCorrect = index === currentGame.answer;
    if (!isCorrect) setCombo(0);
    setFeedback(isCorrect ? "correct" : "wrong");
    
    if (isCorrect) {
      setScore(score + 1);
      maxComboRef.current = Math.max(maxComboRef.current, combo + 1);
      setCombo(combo + 1);
    }
    
    setTimeout(() => {
      setFeedback(null);
      if (currentLevel < games.length - 1) {
        setCurrentLevel(currentLevel + 1);
      } else {
        if (onComplete) onComplete({ score: score + (isCorrect ? 1 : 0), total: games.length, maxCombo: maxComboRef.current });
      }
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 p-4">
      <div className="max-w-lg mx-auto">
        <button
          onClick={onBack}
          className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4"
          data-testid="game-back-btn"
        >
          ← {t.backHome}
        </button>
        
        <ComboPopup combo={combo} />
        <h2 className="text-2xl font-bold text-white text-center mb-2" data-testid="sequence-title">🔢 {t.sequences}</h2>
        
        {/* Progress */}
        <div className="flex justify-between text-white mb-4">
          <span data-testid="sequence-level">{t.level} {currentLevel + 1}/{games.length}</span>
          <span data-testid="sequence-score">{t.score}: {score}</span>
        </div>
        
        <div className="bg-white rounded-3xl p-6 shadow-xl">
          <p className="text-xl text-gray-700 text-center mb-4 font-bold" data-testid="sequence-prompt">
            {t.completeSequence}
          </p>
          
          {/* Sequence */}
          <div className="flex justify-center gap-2 mb-6" data-testid="sequence-display">
            {currentGame.sequence.map((item, idx) => (
              <div
                key={`${item}-${idx}`}
                data-testid={`sequence-item-${idx}`}
                className={`
                  w-16 h-16 flex items-center justify-center rounded-xl text-2xl font-bold
                  ${item === "?" 
                    ? "bg-yellow-100 text-yellow-600 border-4 border-dashed border-yellow-400" 
                    : "bg-purple-100 text-purple-700"
                  }
                `}
              >
                {item}
              </div>
            ))}
          </div>
          
          {/* Options */}
          <div className="flex justify-center gap-4 mb-4">
            {currentGame.options.map((option, idx) => (
              <button
                key={`${option}-${idx}`}
                onClick={() => handleAnswer(idx)}
                disabled={feedback !== null}
                data-testid={`sequence-option-${idx}`}
                className={`
                  w-20 h-20 rounded-2xl text-3xl font-bold transition-all
                  ${feedback === null 
                    ? "bg-gradient-to-br from-purple-400 to-pink-500 text-white hover:scale-110" 
                    : idx === currentGame.answer
                      ? "bg-green-500 text-white ring-4 ring-green-300"
                      : "bg-gray-200 text-gray-500"
                  }
                `}
              >
                {option}
              </button>
            ))}
          </div>
          
          {/* Feedback */}
          {feedback && (
            <div className={`text-center text-xl font-bold ${feedback === "correct" ? "text-green-500" : "text-red-500"}`}>
              {feedback === "correct" ? `✅ ${t.excellent}` : `❌ ${t.tryAgain}`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Memory Game
export function MemoryGame({ t, ageGroup, onBack, onComplete }) {
  const gridSize = ageGroup === "6-7" ? 3 : 4; // 3x4=12 or 4x4=16 cards
  const pairCount = (gridSize * (gridSize === 3 ? 4 : 4)) / 2;
  
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [gameOver, setGameOver] = useState(false);

  const initGame = useCallback(() => {
    const selectedPairs = [...memoryPairs]
      .sort(() => Math.random() - 0.5)
      .slice(0, pairCount);
    const cardPairs = [...selectedPairs, ...selectedPairs]
      .sort(() => Math.random() - 0.5)
      .map((emoji, index) => ({ id: index, emoji, matched: false }));
    setCards(cardPairs);
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setStartTime(Date.now());
    setGameOver(false);
  }, [pairCount]);

  useEffect(() => { initGame(); }, [initGame]);

  const handleCardClick = (id) => {
    if (flipped.length === 2 || flipped.includes(id) || matched.includes(id)) return;
    
    const newFlipped = [...flipped, id];
    setFlipped(newFlipped);
    
    if (newFlipped.length === 2) {
      setMoves(moves + 1);
      const [first, second] = newFlipped;
      
      if (cards[first].emoji === cards[second].emoji) {
        // Match found
        const newMatched = [...matched, first, second];
        setMatched(newMatched);
        setFlipped([]);
        
        // Check if game is complete
        if (newMatched.length === cards.length) {
          setGameOver(true);
          const timeTaken = Math.floor((Date.now() - startTime) / 1000);
          if (onComplete) onComplete({ moves: moves + 1, time: timeTaken });
        }
      } else {
        // No match - flip back after delay
        setTimeout(() => setFlipped([]), 1000);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-4">
      <div className="max-w-lg mx-auto">
        <button
          onClick={onBack}
          className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4"
          data-testid="game-back-btn"
        >
          ← {t.backHome}
        </button>
        
        <h2 className="text-2xl font-bold text-white text-center mb-2" data-testid="memory-title">🧠 {t.memory}</h2>
        <p className="text-white text-center mb-4">{t.findPairs}</p>
        
        {/* Stats */}
        <div className="flex justify-center gap-6 mb-4">
          <div className="bg-white/20 px-4 py-2 rounded-full text-white font-bold" data-testid="memory-moves">
            {t.moves}: {moves}
          </div>
          <div className="bg-white/20 px-4 py-2 rounded-full text-white font-bold" data-testid="memory-pairs">
            {matched.length / 2}/{pairCount} 🎯
          </div>
        </div>
        
        {/* Game Over */}
        {gameOver && (
          <div className="bg-green-100 text-green-700 p-4 rounded-2xl text-center mb-4 font-bold text-xl" data-testid="memory-completed">
            🎉 {t.completed}! {moves} {t.moves}
          </div>
        )}
        
        {/* Card Grid */}
        <div 
          className="grid gap-2 mx-auto"
          style={{ 
            gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
            maxWidth: "400px"
          }}
        >
          {cards.map((card) => {
            const isFlipped = flipped.includes(card.id) || matched.includes(card.id);
            return (
              <button
                key={card.id}
                onClick={() => handleCardClick(card.id)}
                data-testid={`memory-card-${card.id}`}
                className={`
                  aspect-square rounded-xl text-3xl font-bold transition-all duration-300 transform
                  ${isFlipped 
                    ? "bg-white rotate-0" 
                    : "bg-gradient-to-br from-purple-400 to-pink-500 rotate-y-180"
                  }
                  ${matched.includes(card.id) ? "ring-4 ring-green-400" : ""}
                `}
                disabled={matched.includes(card.id)}
              >
                {isFlipped ? card.emoji : "❓"}
              </button>
            );
          })}
        </div>
        
        {/* Restart */}
        <button
          onClick={initGame}
          className="w-full mt-4 py-3 rounded-xl bg-white text-purple-600 font-bold hover:bg-gray-100"
          data-testid="memory-restart-btn"
        >
          🔄 {t.playAgain}
        </button>
      </div>
    </div>
  );
}

// Riddles/Visual Puzzles Game
const SESSION_SIZE = 5;

function shuffleArr(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildRiddleSession(profileId, difficulty) {
  const pool = riddleGames[difficulty];
  const key = `riddle_seen_${profileId || "guest"}_${difficulty}`;
  let seen = [];
  try {
    seen = JSON.parse(localStorage.getItem(key) || "[]");
    if (!Array.isArray(seen)) seen = [];
  } catch (e) { console.warn("Riddle history read failed:", e); seen = []; }

  const allIndices = pool.map((_, i) => i);
  let unseen = allIndices.filter((i) => !seen.includes(i));
  // If not enough unseen riddles for a full session, reset history
  if (unseen.length < SESSION_SIZE) {
    seen = [];
    unseen = allIndices;
  }

  const chosen = shuffleArr(unseen).slice(0, Math.min(SESSION_SIZE, pool.length));
  const newSeen = [...seen, ...chosen];
  try { localStorage.setItem(key, JSON.stringify(newSeen)); } catch (e) { console.warn("Riddle history save failed:", e); }

  // Build riddles with shuffled options
  return chosen.map((idx) => {
    const g = pool[idx];
    const correctText = g.options[g.answer];
    const shuffledOptions = shuffleArr(g.options);
    return {
      ...g,
      options: shuffledOptions,
      answer: shuffledOptions.indexOf(correctText),
    };
  });
}

export function RiddleGame({ t, lang, ageGroup, profileId, onBack, onComplete }) {
  const difficulty = ageGroup === "6-7" ? "easy" : "hard";
  const [games] = useState(() => buildRiddleSession(profileId, difficulty));

  const [currentLevel, setCurrentLevel] = useState(0);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const maxComboRef = useRef(0);
  const [feedback, setFeedback] = useState(null);

  const currentGame = games[currentLevel];

  const handleAnswer = (index) => {
    if (feedback) return;
    
    const isCorrect = index === currentGame.answer;
    if (!isCorrect) setCombo(0);
    setFeedback(isCorrect ? "correct" : "wrong");
    
    if (isCorrect) {
      setScore(score + 1);
      maxComboRef.current = Math.max(maxComboRef.current, combo + 1);
      setCombo(combo + 1);
    }
    
    setTimeout(() => {
      setFeedback(null);
      if (currentLevel < games.length - 1) {
        setCurrentLevel(currentLevel + 1);
      } else {
        if (onComplete) onComplete({ score: score + (isCorrect ? 1 : 0), total: games.length, maxCombo: maxComboRef.current });
      }
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 p-4">
      <div className="max-w-lg mx-auto">
        <button
          onClick={onBack}
          className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4"
          data-testid="game-back-btn"
        >
          ← {t.backHome}
        </button>
        
        <ComboPopup combo={combo} />
        <h2 className="text-2xl font-bold text-white text-center mb-2" data-testid="riddle-title">🤔 {t.riddles}</h2>
        <p className="text-white text-center mb-4 opacity-80">{t.thinkCarefully}</p>
        
        {/* Progress */}
        <div className="flex justify-between text-white mb-4">
          <span>{t.level} {currentLevel + 1}/{games.length}</span>
          <span>{t.score}: {score}</span>
        </div>
        
        <div className="bg-white rounded-3xl p-6 shadow-xl">
          {/* Question */}
          <p className="text-lg text-gray-700 text-center mb-4 font-bold" data-testid="riddle-question">
            {lang === "it" ? currentGame.question_it : currentGame.question_en}
          </p>
          
          {/* Image/Emoji */}
          <div className="text-6xl text-center mb-6" data-testid="riddle-image">
            {currentGame.image}
          </div>
          
          {/* Options */}
          <div className="space-y-3">
            {currentGame.options.map((option, idx) => (
              <button
                key={`${option}-${idx}`}
                onClick={() => handleAnswer(idx)}
                disabled={feedback !== null}
                data-testid={`riddle-option-${idx}`}
                className={`
                  w-full py-4 px-6 rounded-xl text-lg font-bold transition-all
                  ${feedback === null 
                    ? "bg-gradient-to-r from-amber-400 to-orange-500 text-white hover:scale-102" 
                    : idx === currentGame.answer
                      ? "bg-green-500 text-white"
                      : "bg-gray-200 text-gray-500"
                  }
                `}
              >
                {option}
              </button>
            ))}
          </div>
          
          {/* Feedback */}
          {feedback && (
            <div className={`mt-4 text-center text-xl font-bold ${feedback === "correct" ? "text-green-500" : "text-red-500"}`}>
              {feedback === "correct" ? `✅ ${t.excellent}` : `❌ ${t.tryAgain}`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default { OddOneOutGame, SequenceGame, MemoryGame, RiddleGame };
